import React, { useState, useEffect, useRef, useContext } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import LogoutButton from "./Logout";
import useSidebarStore from "../zustand-stores/SidebarStore";
import { retrieve, save } from "../helpers/StorageHelper";

export default function Sidebar({ setOpenJournalSidebar, onToggleSidebar, onMobileClose }) {
  const navigate = useNavigate();
  const { sidebarExpanded, toggleMobileState, sidebarCollapsed, toggleSidebar: storeToggleSidebar } = useSidebarStore();
  const location = useLocation();
  const sidebarRef = useRef(null);
  const sidebarState = sidebarCollapsed ? "collapsed" : "expanded";
  const [isExtended, setIsExtended] = useState(sidebarExpanded);
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 992);
  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth <= 992);
    };
    window.addEventListener("resize", handleResize);
    return () => {
      window.removeEventListener("resize", handleResize);
    };
  }, []);

  useEffect(() => {
    setIsExtended(sidebarExpanded);
  }, [sidebarExpanded]);

  useEffect(() => {
    const currentPath = location.pathname;

    const pageGroups = {
      chat: ["squads", "threads", "add-new-message"],
      "goals-and-targets": ["goals-overview", "body-tent-chat"],
      courses: ["courses", "courses-view", "course-details"],
      assessment: ["assessment-view"],
    };

    const navLinks = document.querySelectorAll(".sidebar .nav-link:not(#journal-link)");

    navLinks.forEach((link) => {
      if (!link) return;

      link.classList.remove("nav-active");
      const linkHref = link.getAttribute("href")?.replace(/^\/+/, "");

      if (!linkHref) return;

      const isMatch =
        currentPath.includes(linkHref) || Object.values(pageGroups).some((subPages) => subPages.some((sub) => currentPath.includes(sub)));

      if (isMatch && currentPath.includes(linkHref)) {
        link.classList.add("nav-active");
      }
    });
  }, [location.pathname]);

  useEffect(() => {
    const handleResponsiveLayout = () => {
      if (window.innerWidth <= 992) {
        save("sidebarMobileState", "full-collapsed");
      }
    };

    handleResponsiveLayout();
    window.addEventListener("resize", handleResponsiveLayout);

    return () => {
      window.removeEventListener("resize", handleResponsiveLayout);
    };
  }, []);

  const toggleSidebar = () => {
    const newCollapsed = !sidebarCollapsed;

    // Update local storage for legacy compatibility
    save("sidebarState", newCollapsed ? "collapsed" : "expanded");

    if (onToggleSidebar) {
      onToggleSidebar(newCollapsed);
    } else {
      storeToggleSidebar(newCollapsed);
    }
  };

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (sidebarRef.current && !sidebarRef.current.contains(event.target) && isExtended) {
        toggleMobileState(false);
      }
    };

    document.addEventListener("click", handleClickOutside);
    return () => {
      document.removeEventListener("click", handleClickOutside);
    };
  }, [isExtended]);

  // Setup custom scrolling
  useEffect(() => {
    const sidebarElement = sidebarRef.current;
    if (!sidebarElement) return;

    const handleWheel = (event) => {
      const isAtTop = sidebarElement.scrollTop === 0;
      const isAtBottom = sidebarElement.scrollHeight - sidebarElement.scrollTop === sidebarElement.clientHeight;

      if (!((isAtTop && event.deltaY < 0) || (isAtBottom && event.deltaY > 0))) {
        sidebarElement.scrollTop += event.deltaY;
      }

      event.preventDefault();
    };

    sidebarElement.addEventListener("wheel", handleWheel, { passive: false });

    return () => {
      sidebarElement.removeEventListener("wheel", handleWheel);
    };
  }, []);

  // Handle journal sidebar toggle
  const handleJournalClick = (e) => {
    e.preventDefault();
    setOpenJournalSidebar((prev) => !prev);
  };

  const mobileCollapsedClass = isMobile && !isExtended ? "full-collapsed" : "";
  const mobileExtendedClass = isMobile && isExtended ? "extended" : "";

  return (
    <div
      className={`sidebar no-scrollbar ${isMobile ? `${mobileCollapsedClass} ${mobileExtendedClass}`.trim() : sidebarState === "collapsed" ? "collapsed" : ""
        }`}
      ref={sidebarRef}
    >
      <div className="d-flex flex-column" style={{ gap: "32px" }}>
        <div className="d-flex flex-column gap-4">
          <div
            className={`d-flex align-items-center ${isMobile ? "justify-content-between" : sidebarState === "collapsed" ? "justify-content-center" : "justify-content-between"
              }`}
          >
            <img
              alt="image-alt"
              className="sidebar-logo"
              src={isMobile ? "/icons/logo-horizontal.svg" : sidebarState === "collapsed" ? "/images/logo.png" : "/icons/logo-horizontal.svg"}
              onClick={() => {
                if (sidebarState === "collapsed" && !isMobile) {
                  toggleSidebar();
                } else {
                  navigate("/dashboard");
                }
              }}
              style={{
                height: !isMobile && sidebarState === "collapsed" ? "43.2px" : "",
                width: !isMobile && sidebarState === "collapsed" ? "38.11px" : "auto",
              }}
            />
            {isMobile && (
              <button
                aria-label="Close sidebar"
                className="btn btn-link p-0 m-0 sidebar-mobile-close"
                style={{
                  display: isExtended ? "block" : "none",
                  background: "transparent",
                  border: "none",
                }}
                onClick={() => {
                  if (onMobileClose) onMobileClose();
                }}
              >
                <img src="/icons/close-btn.svg" alt="close" style={{ width: 24, height: 24 }} />
              </button>
            )}
            {!isMobile && (
              <div
                id="sidebar-menu"
                className="sidebar-arrows"
                style={{
                  display: sidebarState === "collapsed" ? "none" : "block",
                }}
                onClick={toggleSidebar}
              >
                <img alt="image-alt" src="/icons/arrow-left.svg" />
                <img alt="image-alt" src="/icons/arrow-left.svg" />
              </div>
            )}
          </div>
          <div className="">
            <input
              className="sidebar-search text-caption-c2"
              placeholder={!isMobile && sidebarState === "collapsed" ? "" : "Search for..."}
              style={{
                backgroundPosition: !isMobile && sidebarState === "collapsed" ? "center" : "",
              }}
            />
          </div>
        </div>
        <ul
          className="nav flex-column"
          style={{
            marginLeft: !isMobile && sidebarState === "collapsed" ? "0px" : "",
          }}
        >
          <li className="nav-item">
            <Link
              className="nav-link text-body-b1"
              to="/dashboard"
              style={{
                justifyContent: !isMobile && sidebarState === "collapsed" ? "center" : "",
              }}
            >
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
                <path
                  d="M20.8603 8.36985L13.9303 2.82985C12.8603 1.96985 11.1303 1.96985 10.0703 2.81985L3.14027 8.36985C2.36027 8.98985 1.86027 10.2998 2.03027 11.2798L3.36027 19.2398C3.60027 20.6598 4.96027 21.8098 6.40027 21.8098H17.6003C19.0303 21.8098 20.4003 20.6498 20.6403 19.2398L21.9703 11.2798C22.1303 10.2998 21.6303 8.98985 20.8603 8.36985ZM12.0003 15.4998C10.6203 15.4998 9.50027 14.3798 9.50027 12.9998C9.50027 11.6198 10.6203 10.4998 12.0003 10.4998C13.3803 10.4998 14.5003 11.6198 14.5003 12.9998C14.5003 14.3798 13.3803 15.4998 12.0003 15.4998Z"
                  fill="currentColor"
                />
              </svg>
              {!isMobile ? sidebarState !== "collapsed" && "Dashboard" : "Dashboard"}
              <img alt="image-alt" className="active-state-img" src="/icons/active-state-mark.svg" />
            </Link>
          </li>
          <li className="nav-item">
            <Link
              className="nav-link text-body-b1"
              to="/courses"
              style={{
                justifyContent: !isMobile && sidebarState === "collapsed" ? "center" : "",
              }}
            >
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path
                  d="M12.678 19.9589C12.9528 20.0228 12.9779 20.3827 12.7103 20.4719L11.1303 20.9919C7.16034 22.2719 5.07034 21.2019 3.78034 17.2319L2.50034 13.2819C1.22034 9.31187 2.28034 7.21187 6.25034 5.93187L6.77434 5.75834C7.1772 5.62493 7.56927 6.02899 7.45487 6.43766C7.39817 6.64023 7.34362 6.85164 7.29034 7.07188L6.31034 11.2619C5.21034 15.9719 6.82034 18.5719 11.5303 19.6919L12.678 19.9589Z"
                  fill="currentColor"
                />
                <path
                  d="M17.1702 3.20854L15.5002 2.81854C12.1602 2.02854 10.1702 2.67854 9.00018 5.09854C8.70018 5.70854 8.46018 6.44854 8.26018 7.29854L7.28018 11.4885C6.30018 15.6685 7.59018 17.7285 11.7602 18.7185L13.4402 19.1185C14.0202 19.2585 14.5602 19.3485 15.0602 19.3885C18.1802 19.6885 19.8402 18.2285 20.6802 14.6185L21.6602 10.4385C22.6402 6.25854 21.3602 4.18854 17.1702 3.20854ZM15.2902 13.3285C15.2002 13.6685 14.9002 13.8885 14.5602 13.8885C14.5002 13.8885 14.4402 13.8785 14.3702 13.8685L11.4602 13.1285C11.0602 13.0285 10.8202 12.6185 10.9202 12.2185C11.0202 11.8185 11.4302 11.5785 11.8302 11.6785L14.7402 12.4185C15.1502 12.5185 15.3902 12.9285 15.2902 13.3285ZM18.2202 9.94854C18.1302 10.2885 17.8302 10.5085 17.4902 10.5085C17.4302 10.5085 17.3702 10.4985 17.3002 10.4885L12.4502 9.25854C12.0502 9.15854 11.8102 8.74854 11.9102 8.34854C12.0102 7.94854 12.4202 7.70854 12.8202 7.80854L17.6702 9.03854C18.0802 9.12854 18.3202 9.53854 18.2202 9.94854Z"
                  fill="currentColor"
                />
              </svg>
              {!isMobile ? sidebarState !== "collapsed" && "Courses" : "courses"}
              <img alt="image-alt" className="active-state-img" src="/icons/active-state-mark.svg" />
            </Link>
          </li>
          <li className="nav-item">
            <Link
              className="nav-link text-body-b1"
              to="/chat"
              style={{
                justifyContent: !isMobile && sidebarState === "collapsed" ? "center" : "",
              }}
            >
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path
                  d="M13.19 6H6.79C6.53 6 6.28 6.01 6.04 6.04C3.35 6.27 2 7.86 2 10.79V14.79C2 18.79 3.6 19.58 6.79 19.58H7.19C7.41 19.58 7.7 19.73 7.83 19.9L9.03 21.5C9.56 22.21 10.42 22.21 10.95 21.5L12.15 19.9C12.3 19.7 12.54 19.58 12.79 19.58H13.19C16.12 19.58 17.71 18.24 17.94 15.54C17.97 15.3 17.98 15.05 17.98 14.79V10.79C17.98 7.6 16.38 6 13.19 6ZM6.5 14C5.94 14 5.5 13.55 5.5 13C5.5 12.45 5.95 12 6.5 12C7.05 12 7.5 12.45 7.5 13C7.5 13.55 7.05 14 6.5 14ZM9.99 14C9.43 14 8.99 13.55 8.99 13C8.99 12.45 9.44 12 9.99 12C10.54 12 10.99 12.45 10.99 13C10.99 13.55 10.55 14 9.99 14ZM13.49 14C12.93 14 12.49 13.55 12.49 13C12.49 12.45 12.94 12 13.49 12C14.04 12 14.49 12.45 14.49 13C14.49 13.55 14.04 14 13.49 14Z"
                  fill="currentColor"
                />
                <path
                  d="M21.9802 6.79V10.79C21.9802 12.79 21.3602 14.15 20.1202 14.9C19.8202 15.08 19.4702 14.84 19.4702 14.49L19.4802 10.79C19.4802 6.79 17.1902 4.5 13.1902 4.5L7.10025 4.51C6.75025 4.51 6.51025 4.16 6.69025 3.86C7.44025 2.62 8.80025 2 10.7902 2H17.1902C20.3802 2 21.9802 3.6 21.9802 6.79Z"
                  fill="currentColor"
                />
              </svg>
              {!isMobile ? sidebarState !== "collapsed" && "Chat" : "Chat"}
              <img alt="image-alt" className="active-state-img" src="/icons/active-state-mark.svg" />
            </Link>
          </li>
          <li className="nav-item">
            <Link
              className="nav-link text-body-b1"
              to="/tribe"
              style={{
                justifyContent: !isMobile && sidebarState === "collapsed" ? "center" : "",
              }}
            >
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path
                  d="M17.53 7.77C17.46 7.76 17.39 7.76 17.32 7.77C15.77 7.72 14.54 6.45 14.54 4.89C14.54 3.3 15.83 2 17.43 2C19.02 2 20.32 3.29 20.32 4.89C20.31 6.45 19.08 7.72 17.53 7.77Z"
                  fill="currentColor"
                />
                <path
                  d="M20.7896 14.7004C19.6696 15.4504 18.0996 15.7304 16.6496 15.5404C17.0296 14.7204 17.2296 13.8104 17.2396 12.8504C17.2396 11.8504 17.0196 10.9004 16.5996 10.0704C18.0796 9.8704 19.6496 10.1504 20.7796 10.9004C22.3596 11.9404 22.3596 13.6504 20.7896 14.7004Z"
                  fill="currentColor"
                />
                <path
                  d="M6.44016 7.77C6.51016 7.76 6.58016 7.76 6.65016 7.77C8.20016 7.72 9.43016 6.45 9.43016 4.89C9.43016 3.29 8.14016 2 6.54016 2C4.95016 2 3.66016 3.29 3.66016 4.89C3.66016 6.45 4.89016 7.72 6.44016 7.77Z"
                  fill="currentColor"
                />
                <path
                  d="M6.55012 12.8506C6.55012 13.8206 6.76012 14.7406 7.14012 15.5706C5.73012 15.7206 4.26012 15.4206 3.18012 14.7106C1.60012 13.6606 1.60012 11.9506 3.18012 10.9006C4.25012 10.1806 5.76012 9.89059 7.18012 10.0506C6.77012 10.8906 6.55012 11.8406 6.55012 12.8506Z"
                  fill="currentColor"
                />
                <path
                  d="M12.1198 15.87C12.0398 15.86 11.9498 15.86 11.8598 15.87C10.0198 15.81 8.5498 14.3 8.5498 12.44C8.5598 10.54 10.0898 9 11.9998 9C13.8998 9 15.4398 10.54 15.4398 12.44C15.4298 14.3 13.9698 15.81 12.1198 15.87Z"
                  fill="currentColor"
                />
                <path
                  d="M8.8698 17.9406C7.3598 18.9506 7.3598 20.6106 8.8698 21.6106C10.5898 22.7606 13.4098 22.7606 15.1298 21.6106C16.6398 20.6006 16.6398 18.9406 15.1298 17.9406C13.4198 16.7906 10.5998 16.7906 8.8698 17.9406Z"
                  fill="currentColor"
                />
              </svg>

              {!isMobile ? (
                sidebarState !== "collapsed" && (
                  <>
                    <span className="nav-text">Tribe</span>
                    <img alt="Tribe active indicator" className="active-state-img" src="/icons/active-state-mark.svg" />
                  </>
                )
              ) : (
                <>
                  <span className="nav-text">Tribe</span>
                  <img alt="Tribe active indicator" className="active-state-img" src="/icons/active-state-mark.svg" />
                </>
              )}
            </Link>
          </li>
          <li className="nav-item" style={{ cursor: "pointer" }}>
            <div
              id="journal-link"
              onClick={handleJournalClick}
              className={`nav-link text-body-b1 ${location.pathname.includes("journal") || location.pathname.includes("journal-library") ? "nav-active" : ""
                }`}
              style={{
                justifyContent: !isMobile && sidebarState === "collapsed" ? "center" : "",
              }}
            >
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
                <path
                  d="M16 4.25C16 5.49 14.99 6.5 13.75 6.5H10.25C9.63 6.5 9.07 6.25 8.66 5.84C8.25 5.43 8 4.87 8 4.25C8 3.01 9.01 2 10.25 2H13.75C14.37 2 14.93 2.25 15.34 2.66C15.75 3.07 16 3.63 16 4.25Z"
                  fill="currentColor"
                />
                <path
                  d="M18.83 5.02874C18.6 4.83874 18.34 4.68874 18.06 4.57874C17.77 4.46874 17.48 4.69874 17.42 4.99874C17.08 6.70874 15.57 7.99874 13.75 7.99874H10.25C9.25 7.99874 8.31 7.60874 7.6 6.89874C7.08 6.37874 6.72 5.71874 6.58 5.00874C6.52 4.70874 6.22 4.46874 5.93 4.58874C4.77 5.05874 4 6.11874 4 8.24874V17.9987C4 20.9987 5.79 21.9987 8 21.9987H16C18.21 21.9987 20 20.9987 20 17.9987V8.24874C20 6.61874 19.55 5.61874 18.83 5.02874ZM8 12.2487H12C12.41 12.2487 12.75 12.5887 12.75 12.9987C12.75 13.4087 12.41 13.7487 12 13.7487H8C7.59 13.7487 7.25 13.4087 7.25 12.9987C7.25 12.5887 7.59 12.2487 8 12.2487ZM16 17.7487H8C7.59 17.7487 7.25 17.4087 7.25 16.9987C7.25 16.5887 7.59 16.2487 8 16.2487H16C16.41 16.2487 16.75 16.5887 16.75 16.9987C16.75 17.4087 16.41 17.7487 16 17.7487Z"
                  fill="currentColor"
                />
              </svg>
              {!isMobile ? sidebarState !== "collapsed" && "Journal" : "Journal"}
              <img alt="image-alt" className="active-state-img" src="/icons/active-state-mark.svg" />
            </div>
          </li>
          <li className="nav-item">
            <Link
              className="nav-link text-body-b1"
              to="/habit-tracker"
              style={{
                justifyContent: !isMobile && sidebarState === "collapsed" ? "center" : "",
              }}
            >
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path
                  d="M16.7502 3.56V2C16.7502 1.59 16.4102 1.25 16.0002 1.25C15.5902 1.25 15.2502 1.59 15.2502 2V3.5H8.75023V2C8.75023 1.59 8.41023 1.25 8.00023 1.25C7.59023 1.25 7.25023 1.59 7.25023 2V3.56C4.55023 3.81 3.24023 5.42 3.04023 7.81C3.02023 8.1 3.26023 8.34 3.54023 8.34H20.4602C20.7502 8.34 20.9902 8.09 20.9602 7.81C20.7602 5.42 19.4502 3.81 16.7502 3.56Z"
                  fill="currentColor"
                />
                <path
                  d="M20 9.83984H4C3.45 9.83984 3 10.2898 3 10.8398V16.9998C3 19.9998 4.5 21.9998 8 21.9998H16C19.5 21.9998 21 19.9998 21 16.9998V10.8398C21 10.2898 20.55 9.83984 20 9.83984ZM9.21 18.2098C9.11 18.2998 9 18.3698 8.88 18.4198C8.76 18.4698 8.63 18.4998 8.5 18.4998C8.37 18.4998 8.24 18.4698 8.12 18.4198C8 18.3698 7.89 18.2998 7.79 18.2098C7.61 18.0198 7.5 17.7598 7.5 17.4998C7.5 17.2398 7.61 16.9798 7.79 16.7898C7.89 16.6998 8 16.6298 8.12 16.5798C8.36 16.4798 8.64 16.4798 8.88 16.5798C9 16.6298 9.11 16.6998 9.21 16.7898C9.39 16.9798 9.5 17.2398 9.5 17.4998C9.5 17.7598 9.39 18.0198 9.21 18.2098ZM9.42 14.3798C9.37 14.4998 9.3 14.6098 9.21 14.7098C9.11 14.7998 9 14.8698 8.88 14.9198C8.76 14.9698 8.63 14.9998 8.5 14.9998C8.37 14.9998 8.24 14.9698 8.12 14.9198C8 14.8698 7.89 14.7998 7.79 14.7098C7.7 14.6098 7.63 14.4998 7.58 14.3798C7.53 14.2598 7.5 14.1298 7.5 13.9998C7.5 13.8698 7.53 13.7398 7.58 13.6198C7.63 13.4998 7.7 13.3898 7.79 13.2898C7.89 13.1998 8 13.1298 8.12 13.0798C8.36 12.9798 8.64 12.9798 8.88 13.0798C9 13.1298 9.11 13.1998 9.21 13.2898C9.3 13.3898 9.37 13.4998 9.42 13.6198C9.47 13.7398 9.5 13.8698 9.5 13.9998C9.5 14.1298 9.47 14.2598 9.42 14.3798ZM12.71 14.7098C12.61 14.7998 12.5 14.8698 12.38 14.9198C12.26 14.9698 12.13 14.9998 12 14.9998C11.87 14.9998 11.74 14.9698 11.62 14.9198C11.5 14.8698 11.39 14.7998 11.29 14.7098C11.11 14.5198 11 14.2598 11 13.9998C11 13.7398 11.11 13.4798 11.29 13.2898C11.39 13.1998 11.5 13.1298 11.62 13.0798C11.86 12.9698 12.14 12.9698 12.38 13.0798C12.5 13.1298 12.61 13.1998 12.71 13.2898C12.89 13.4798 13 13.7398 13 13.9998C13 14.2598 12.89 14.5198 12.71 14.7098Z"
                  fill="currentColor"
                />
              </svg>
              {!isMobile ? sidebarState !== "collapsed" && "Habit Tracker" : "Habit Tracker"}
              <img alt="image-alt" className="active-state-img" src="/icons/active-state-mark.svg" />
            </Link>
          </li>
          <li className="nav-item">
            <Link
              className="nav-link text-body-b1"
              to="/task-management"
              style={{
                justifyContent: !isMobile && sidebarState === "collapsed" ? "center" : "",
              }}
            >
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path
                  d="M16.19 2H7.81C4.17 2 2 4.17 2 7.81V16.19C2 19.83 4.17 22 7.81 22H16.19C19.83 22 22 19.83 22 16.19V7.81C22 4.17 19.83 2 16.19 2ZM9.97 14.9L7.72 17.15C7.57 17.3 7.38 17.37 7.19 17.37C7 17.37 6.8 17.3 6.66 17.15L5.91 16.4C5.61 16.11 5.61 15.63 5.91 15.34C6.2 15.05 6.67 15.05 6.97 15.34L7.19 15.56L8.91 13.84C9.2 13.55 9.67 13.55 9.97 13.84C10.26 14.13 10.26 14.61 9.97 14.9ZM9.97 7.9L7.72 10.15C7.57 10.3 7.38 10.37 7.19 10.37C7 10.37 6.8 10.3 6.66 10.15L5.91 9.4C5.61 9.11 5.61 8.63 5.91 8.34C6.2 8.05 6.67 8.05 6.97 8.34L7.19 8.56L8.91 6.84C9.2 6.55 9.67 6.55 9.97 6.84C10.26 7.13 10.26 7.61 9.97 7.9ZM17.56 16.62H12.31C11.9 16.62 11.56 16.28 11.56 15.87C11.56 15.46 11.9 15.12 12.31 15.12H17.56C17.98 15.12 18.31 15.46 18.31 15.87C18.31 16.28 17.98 16.62 17.56 16.62ZM17.56 9.62H12.31C11.9 9.62 11.56 9.28 11.56 8.87C11.56 8.46 11.9 8.12 12.31 8.12H17.56C17.98 8.12 18.31 8.46 18.31 8.87C18.31 9.28 17.98 9.62 17.56 9.62Z"
                  fill="currentColor"
                />
              </svg>
              {!isMobile ? sidebarState !== "collapsed" && "Task Management" : "Task Management"}
              <img alt="image-alt" className="active-state-img" src="/icons/active-state-mark.svg" />
            </Link>
          </li>
          <li className="nav-item">
            <Link
              className="nav-link text-body-b1"
              to="/goals-and-targets"
              style={{
                justifyContent: !isMobile && sidebarState === "collapsed" ? "center" : "",
              }}
            >
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path
                  d="M16.65 5.71875H7.35C4.4 5.71875 2 8.11875 2 11.0687V16.6488C2 19.5988 4.4 21.9988 7.35 21.9988H16.65C19.6 21.9988 22 19.5988 22 16.6488V11.0687C22 8.11875 19.6 5.71875 16.65 5.71875ZM14.5 12.0188C14.5 11.4688 14.95 11.0188 15.5 11.0188C16.05 11.0188 16.5 11.4688 16.5 12.0188C16.5 12.5688 16.05 13.0287 15.5 13.0287C14.95 13.0287 14.5 12.5888 14.5 12.0388V12.0188ZM10.13 16.0688C9.98 16.2188 9.79 16.2888 9.6 16.2888C9.41 16.2888 9.22 16.2188 9.07 16.0688L8.04 15.0387L7.05 16.0288C6.9 16.1788 6.71 16.2488 6.52 16.2488C6.33 16.2488 6.14 16.1788 5.99 16.0288C5.7 15.7388 5.7 15.2587 5.99 14.9688L6.98 13.9788L6.02 13.0188C5.73 12.7288 5.73 12.2488 6.02 11.9588C6.31 11.6688 6.79 11.6688 7.08 11.9588L8.04 12.9188L9.03 11.9288C9.32 11.6388 9.8 11.6388 10.09 11.9288C10.38 12.2188 10.38 12.6988 10.09 12.9887L9.1 13.9788L10.13 15.0088C10.42 15.2988 10.42 15.7788 10.13 16.0688ZM13.54 14.9988C12.99 14.9988 12.53 14.5488 12.53 13.9988C12.53 13.4488 12.97 12.9988 13.52 12.9988H13.54C14.09 12.9988 14.54 13.4488 14.54 13.9988C14.54 14.5488 14.1 14.9988 13.54 14.9988ZM15.5 16.9688C14.95 16.9688 14.5 16.5288 14.5 15.9788V15.9587C14.5 15.4087 14.95 14.9587 15.5 14.9587C16.05 14.9587 16.5 15.4087 16.5 15.9587C16.5 16.5087 16.06 16.9688 15.5 16.9688ZM17.48 14.9988C16.93 14.9988 16.47 14.5488 16.47 13.9988C16.47 13.4488 16.91 12.9988 17.46 12.9988H17.48C18.03 12.9988 18.48 13.4488 18.48 13.9988C18.48 14.5488 18.04 14.9988 17.48 14.9988Z"
                  fill="currentColor"
                />
                <path
                  d="M13.6404 2.71L13.6304 3.65C13.6204 4.53 12.8904 5.26 12.0004 5.26C11.8504 5.26 11.7604 5.36 11.7604 5.49C11.7604 5.62 11.8604 5.72 11.9904 5.72H10.3804C10.3704 5.65 10.3604 5.57 10.3604 5.49C10.3604 4.59 11.0904 3.86 11.9804 3.86C12.1304 3.86 12.2304 3.76 12.2304 3.63L12.2404 2.69C12.2504 2.31 12.5604 2 12.9404 2H12.9504C13.3404 2 13.6404 2.32 13.6404 2.71Z"
                  fill="currentColor"
                />
              </svg>
              {!isMobile ? sidebarState !== "collapsed" && "Goals and Targets" : "Goals and Targets"}
              <img alt="image-alt" className="active-state-img" src="/icons/active-state-mark.svg" />
            </Link>
          </li>
          <li className="nav-item">
            <Link
              className="nav-link text-body-b1"
              to="/power-score-stats"
              style={{
                justifyContent: !isMobile && sidebarState === "collapsed" ? "center" : "",
              }}
            >
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path
                  d="M16.19 2H7.81C4.17 2 2 4.17 2 7.81V16.18C2 19.83 4.17 22 7.81 22H16.18C19.82 22 21.99 19.83 21.99 16.19V7.81C22 4.17 19.83 2 16.19 2ZM9.91 16.19C9.91 16.83 9.39 17.35 8.74 17.35C8.1 17.35 7.58 16.83 7.58 16.19V12.93C7.58 12.29 8.1 11.77 8.74 11.77C9.39 11.77 9.91 12.29 9.91 12.93V16.19ZM16.42 16.19C16.42 16.83 15.9 17.35 15.26 17.35C14.61 17.35 14.09 16.83 14.09 16.19V7.81C14.09 7.17 14.61 6.65 15.26 6.65C15.9 6.65 16.42 7.17 16.42 7.81V16.19Z"
                  fill="currentColor"
                />
              </svg>
              {!isMobile ? (
                sidebarState !== "collapsed" && <span className="nav-link-text">Power Score Stats</span>
              ) : (
                <span className="nav-link-text">Power Score Stats</span>
              )}
              <img
                alt="Power Score Stats"
                className="active-state-img"
                src="/icons/active-state-mark.svg"
                style={{
                  display: !isMobile && sidebarState === "collapsed" ? "none" : "block",
                }}
              />
            </Link>
          </li>
          <li className="nav-item">
            <Link
              className="nav-link text-body-b1"
              to="/assessment"
              style={{
                justifyContent: !isMobile && sidebarState === "collapsed" ? "center" : "",
              }}
            >
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path
                  d="M20.5 16V18.5C20.5 20.43 18.93 22 17 22H7C5.07 22 3.5 20.43 3.5 18.5V17.85C3.5 16.28 4.78 15 6.35 15H19.5C20.05 15 20.5 15.45 20.5 16Z"
                  fill="currentColor"
                />
                <path
                  d="M15.5 2H8.5C4.5 2 3.5 3 3.5 7V14.58C4.26 13.91 5.26 13.5 6.35 13.5H19.5C20.05 13.5 20.5 13.05 20.5 12.5V7C20.5 3 19.5 2 15.5 2ZM13 10.75H8C7.59 10.75 7.25 10.41 7.25 10C7.25 9.59 7.59 9.25 8 9.25H13C13.41 9.25 13.75 9.59 13.75 10C13.75 10.41 13.41 10.75 13 10.75ZM16 7.25H8C7.59 7.25 7.25 6.91 7.25 6.5C7.25 6.09 7.59 5.75 8 5.75H16C16.41 5.75 16.75 6.09 16.75 6.5C16.75 6.91 16.41 7.25 16 7.25Z"
                  fill="currentColor"
                />
              </svg>
              {!isMobile ? (
                sidebarState !== "collapsed" && (
                  <>
                    <span className="nav-text">Assessment</span>
                    <img alt="Assessment active indicator" className="active-state-img" src="/icons/active-state-mark.svg" />
                  </>
                )
              ) : (
                <>
                  <span className="nav-text">Assessment</span>
                  <img alt="Assessment active indicator" className="active-state-img" src="/icons/active-state-mark.svg" />
                </>
              )}
            </Link>
          </li>

          <li className="nav-item">
            <Link
              className="nav-link text-body-b1"
              to="/settings"
              style={{
                justifyContent: !isMobile && sidebarState === "collapsed" ? "center" : "",
              }}
            >
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none" className="nav-icon" aria-hidden="true">
                <path
                  d="M20.1 9.2214C18.29 9.2214 17.55 7.9414 18.45 6.3714C18.97 5.4614 18.66 4.3014 17.75 3.7814L16.02 2.7914C15.23 2.3214 14.21 2.6014 13.74 3.3914L13.63 3.5814C12.73 5.1514 11.25 5.1514 10.34 3.5814L10.23 3.3914C9.78 2.6014 8.76 2.3214 7.97 2.7914L6.24 3.7814C5.33 4.3014 5.02 5.4714 5.54 6.3814C6.45 7.9414 5.71 9.2214 3.9 9.2214C2.86 9.2214 2 10.0714 2 11.1214V12.8814C2 13.9214 2.85 14.7814 3.9 14.7814C5.71 14.7814 6.45 16.0614 5.54 17.6314C5.02 18.5414 5.33 19.7014 6.24 20.2214L7.97 21.2114C8.76 21.6814 9.78 21.4014 10.25 20.6114L10.36 20.4214C11.26 18.8514 12.74 18.8514 13.65 20.4214L13.76 20.6114C14.23 21.4014 15.25 21.6814 16.04 21.2114L17.77 20.2214C18.68 19.7014 18.99 18.5314 18.47 17.6314C17.56 16.0614 18.3 14.7814 20.11 14.7814C21.15 14.7814 22.01 13.9314 22.01 12.8814V11.1214C22 10.0814 21.15 9.2214 20.1 9.2214ZM12 15.2514C10.21 15.2514 8.75 13.7914 8.75 12.0014C8.75 10.2114 10.21 8.7514 12 8.7514C13.79 8.7514 15.25 10.2114 15.25 12.0014C15.25 13.7914 13.79 15.2514 12 15.2514Z"
                  fill="currentColor"
                />
              </svg>

              {!isMobile ? (
                sidebarState !== "collapsed" && (
                  <>
                    <span className="nav-text">Settings</span>
                    <img alt="Settings active indicator" className="active-state-img" src="/icons/active-state-mark.svg" />
                  </>
                )
              ) : (
                <>
                  <span className="nav-text">Settings</span>
                  <img alt="Settings active indicator" className="active-state-img" src="/icons/active-state-mark.svg" />
                </>
              )}
            </Link>
          </li>
          <hr className="sidebar-hr" />
          {/* logout */}
          {/* <li className="nav-item">
            <Link
              className="nav-link text-body-b1"
              to="/login"
              onClick={() => {}}
            >
              <svg
                width="24"
                height="24"
                viewBox="0 0 24 24"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  d="M16.8 2H14.2C11 2 9 4 9 7.2V11.25H15.25C15.66 11.25 16 11.59 16 12C16 12.41 15.66 12.75 15.25 12.75H9V16.8C9 20 11 22 14.2 22H16.79C19.99 22 21.99 20 21.99 16.8V7.2C22 4 20 2 16.8 2Z"
                  fill="currentColor"
                />
                <path
                  d="M4.56043 11.2498L6.63043 9.17984C6.78043 9.02984 6.85043 8.83984 6.85043 8.64984C6.85043 8.45984 6.78043 8.25984 6.63043 8.11984C6.34043 7.82984 5.86043 7.82984 5.57043 8.11984L2.22043 11.4698C1.93043 11.7598 1.93043 12.2398 2.22043 12.5298L5.57043 15.8798C5.86043 16.1698 6.34043 16.1698 6.63043 15.8798C6.92043 15.5898 6.92043 15.1098 6.63043 14.8198L4.56043 12.7498H9.00043V11.2498H4.56043Z"
                  fill="currentColor"
                />
              </svg>
              Logout
              <img
                alt="image-alt"
                className="active-state-img"
                src="/icons/active-state-mark.svg"
              />
            </Link>
          </li> */}
          <LogoutButton sidebarState={sidebarState} />
        </ul>
      </div>
    </div>
  );
}
